import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { VariablesOf } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import type { EngagementType, ReactionType } from '../../graphqlEnums'

/**
 * `id`, `status`, and `reactions` are `Engagement` interface fields, so
 * selecting them directly on the returned engagement covers every engagement
 * type. Apollo merges the updated reactions into the cached engagement by id,
 * so the card reflects the new state without a manual cache write.
 */
export const ReactToEngagementMutationGQL = graphql(/* GraphQL */ `
  mutation ReactToEngagement($input: ReactToEngagementInput!) {
    reactToEngagement(input: $input) {
      __typename
      ... on ReactToEngagementSuccess {
        engagement {
          __typename
          id
          status
          reactions {
            type
          }
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

/**
 * Whether a reaction is being added or removed (`'add'` | `'remove'`), derived
 * from the `action` input field on {@link ReactToEngagementMutationGQL}.
 */
type ReactionAction = VariablesOf<
  typeof ReactToEngagementMutationGQL
>['input']['action']

type ReactToEngagementArgs = {
  engagementId: string
  engagementType: EngagementType
  /** `true` adds the reaction, `false` removes it. */
  isActiveReaction: boolean
  /**
   * The reaction to apply. Defaults to `'like'`. Networks without
   * multi-reaction support reject other values; mentions accept `like` only.
   */
  reactionType?: ReactionType
}

type UseReactToEngagementResult = {
  reactToEngagement: (args: ReactToEngagementArgs) => Promise<void>
  loading: boolean
}

/**
 * Like/unlike an engagement. Kind-agnostic — accepts `mention` or `comment` via
 * `engagementType`.
 *
 * Resolves on success and throws on failure so callers can roll back their
 * optimistic UI. The thrown error carries what the API said about the failure
 * — a deleted engagement, an expired channel — so call sites can show it as
 * is. Mirrors the reply path (`useMentionReplySubmitStrategy`).
 */
export const useReactToEngagement = (): UseReactToEngagementResult => {
  const [mutate, { loading }] = useTypedMutation(
    ReactToEngagementMutationGQL,
    (data) => data.reactToEngagement,
    {
      successTypename: 'ReactToEngagementSuccess',
    },
  )

  const reactToEngagement = async ({
    engagementId,
    engagementType,
    isActiveReaction,
    reactionType = 'like',
  }: ReactToEngagementArgs): Promise<void> => {
    const action: ReactionAction = isActiveReaction ? 'add' : 'remove'

    // Kind-agnostic hook, so the toast noun follows the engagement type.
    const errorMessage =
      engagementType === 'comment'
        ? 'An error occurred while reacting to the comment. Please try again.'
        : 'An error occurred while reacting to the mention. Please try again.'

    let result
    try {
      result = await mutate({
        variables: {
          input: { engagementId, engagementType, action, reactionType },
        },
      })
    } catch (error) {
      // Network or unexpected error during mutation.
      // eslint-disable-next-line no-console
      console.error('Unexpected error while reacting to engagement:', error)
      toast.error(errorMessage, { duration: 2_000 })
      throw new Error(errorMessage, { cause: error })
    }

    if (!result.success) {
      // eslint-disable-next-line no-console
      console.error('Error while reacting to engagement:', result.error)

      // A typed payload error explains the failure in the user's terms (e.g.
      // the mention was deleted), so it wins over the generic sentence. The
      // synthetic `MutationError` wraps transport failures, whose text is not
      // for users.
      const apiMessage =
        result.error?.__typename === 'VoidMutationError'
          ? result.error.message.trim()
          : ''
      const message = apiMessage || errorMessage

      toast.error(message, { duration: 2_000 })
      throw new Error(message)
    }
  }

  return {
    reactToEngagement,
    loading,
  }
}
